import {
  Button,
  Stack,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Paper,
  Box,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { TokenFeeScheduleUpdateTransaction, Transaction } from "@hashgraph/sdk";
import { useWalletInterface } from "../services/wallets/useWalletInterface";
import { computeRequiredSignatures } from "../services/computeRequiredSignatures";

function formatSeconds(s: number) {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m > 0 ? `${m}:${String(r).padStart(2, "0")}` : `${r}s`;
}

function base64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

type ParsedTxInfo = {
  type: string;
  txId: string;
  validStartSeconds?: number;
  nodeAccountId?: string;
  tx: Transaction;
};

type SigCalcState =
  | { status: "idle" }
  | { status: "calculating" }
  | { status: "ready"; required: number }
  | { status: "error"; message: string };

function getTxType(tx: Transaction) {
  if (tx instanceof TokenFeeScheduleUpdateTransaction) return "TokenFeeScheduleUpdateTransaction";
  return "Not supported";
}

export default function ExecuteMultiSig() {
  const { walletInterface } = useWalletInterface();

  const [txBase64, setTxBase64] = useState("");

  const [txError, setTxError] = useState<string>("");
  const [txInfo, setTxInfo] = useState<ParsedTxInfo | null>(null);

  const [sigCalc, setSigCalc] = useState<SigCalcState>({ status: "idle" });
  const [sigs, setSigs] = useState<string[]>([]);

  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState<"waiting" | "executing" | "done" | "error">("waiting");
  const [remaining, setRemaining] = useState<number>(0);
  const [resultText, setResultText] = useState<string>("");

  // 1) Parse tx
  useEffect(() => {
    const v = txBase64.trim();
    if (!v) {
      setTxInfo(null);
      setTxError("");
      setSigCalc({ status: "idle" });
      setSigs([]);
      return;
    }

    try {
      const bytes = base64ToBytes(v);
      const tx = Transaction.fromBytes(bytes);

      const txId = tx.transactionId?.toString?.() ?? "(no txId)";
      const type = getTxType(tx);
      const validStartSeconds = tx.transactionId?.validStart?.seconds?.toNumber?.();
      const nodeAccountId = tx.nodeAccountIds?.[0]?.toString?.();

      setTxInfo({ type, txId, validStartSeconds, nodeAccountId, tx });
      setTxError("");

      // start calc right away (unless unsupported)
      if (type === "Not supported") {
        setSigCalc({ status: "error", message: "Transaction type not supported" });
      } else {
        setSigCalc({ status: "calculating" });
      }
    } catch (e: any) {
      setTxInfo(null);
      setTxError(e?.message ?? "Invalid transaction base64");
      setSigCalc({ status: "idle" });
      setSigs([]);
    }
  }, [txBase64]);

  // 2) Compute required sigs (no more reverting to idle)
  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!txInfo) return;
      if (txInfo.type === "Not supported") return;

      setSigCalc({ status: "calculating" });

      try {
        const required = await computeRequiredSignatures(txInfo.tx);
        if (cancelled) return;

        if (required <= 0) {
          setSigCalc({ status: "error", message: "Required signature count is 0 (invalid)" });
          setSigs([]);
          return;
        }

        setSigCalc({ status: "ready", required });
      } catch (e: any) {
        if (cancelled) return;
        setSigCalc({ status: "error", message: e?.message ?? String(e) });
        setSigs([]);
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [txInfo]);

  // 3) Resize signature fields to required count when ready
  const requiredSigCount = sigCalc.status === "ready" ? sigCalc.required : null;

  useEffect(() => {
    if (requiredSigCount === null) return;

    setSigs((prev) => {
      const next = prev.slice(0, requiredSigCount);
      while (next.length < requiredSigCount) next.push("");
      return next;
    });
  }, [requiredSigCount]);

  const allSigsFilled = useMemo(
    () => requiredSigCount !== null && sigs.length === requiredSigCount && sigs.every((s) => s.trim().length > 0),
    [sigs, requiredSigCount]
  );

  const canExecute =
    !!walletInterface &&
    !!txInfo &&
    !txError &&
    sigCalc.status === "ready" &&
    sigCalc.required > 0 &&
    allSigsFilled;

  const onExecute = async () => {
    if (!canExecute || !walletInterface || !txInfo) return;

    setOpen(true);
    setPhase("waiting");
    setRemaining(0);
    setResultText("");

    try {
      const res = await walletInterface.exectueTransaction(
        txBase64.trim(),
        sigs.map((s) => s.trim()),
        (sec) => {
          setRemaining(Math.max(sec, 0));
          setPhase(sec > 0 ? "waiting" : "executing");
        }
      );

      setResultText(
        res
          ? `Executed\nTransactionId: ${res.txId}\nStatus: ${res.status}`
          : `Error: Not executed`
      );
      setPhase("done");
    } catch (e: any) {
      setResultText(e?.message ?? String(e));
      setPhase("error");
    }
  };

  const canClose = phase === "done" || phase === "error";

  return (
    <Stack spacing={3} alignItems="center">
      <Typography variant="h5">Execute Multisig Transaction</Typography>

      <TextField
        label="Transaction (Base64)"
        value={txBase64}
        onChange={(e) => setTxBase64(e.target.value)}
        multiline
        minRows={3}
        error={!!txError}
        helperText={txError || "Paste base64-encoded Hedera transaction"}
        sx={{ width: "100%", maxWidth: 600 }}
      />

      {txInfo && (
        <Paper variant="outlined" sx={{ width: "100%", maxWidth: 600, p: 1.5, borderRadius: 2 }}>
          <Typography sx={{ fontWeight: 600, mb: 0.5 }}>Parsed transaction</Typography>

          <Typography sx={{ fontFamily: "monospace", fontSize: 13, whiteSpace: "pre-wrap" }}>
            Type: {txInfo.type}
            {"\n"}TxId: {txInfo.txId}
            {txInfo.nodeAccountId ? `\nNode: ${txInfo.nodeAccountId}` : ""}
            {txInfo.validStartSeconds ? `\nValidStart: ${new Date(txInfo.validStartSeconds*1000).toLocaleString()}` : ""}
          </Typography>

          <Box sx={{ mt: 1.5 }}>
            <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
              Required signatures:{" "}
              {sigCalc.status === "calculating" && "Calculating..."}
              {sigCalc.status === "ready" && sigCalc.required}
              {sigCalc.status === "idle" && "—"}
              {sigCalc.status === "error" && `Error: ${sigCalc.message}`}
            </Typography>

            {sigCalc.status === "error" && (
              <Typography sx={{ mt: 1 }} color="error">
                Cannot show signature fields until required signature count is known.
              </Typography>
            )}
          </Box>
        </Paper>
      )}

      {/* Signature inputs only when we have a valid required count */}
      {requiredSigCount !== null && (
        <Stack spacing={2} sx={{ width: "100%", maxWidth: 600 }}>
          {sigs.map((val, idx) => (
            <TextField
              key={idx}
              label={`Signature ${idx + 1}`}
              value={val}
              onChange={(e) => {
                const next = [...sigs];
                next[idx] = e.target.value;
                setSigs(next);
              }}
              sx={{ width: "100%" }}
            />
          ))}
        </Stack>
      )}

      <Button variant="contained" disabled={!canExecute} onClick={onExecute}>
        Execute
      </Button>

      <Dialog open={open} onClose={canClose ? () => setOpen(false) : undefined}>
        <DialogTitle>
          {phase === "waiting" && "Waiting for Tx valid start"}
          {phase === "executing" && "Executing transaction"}
          {phase === "done" && "✅ Executed"}
          {phase === "error" && "❌ Execution failed"}
        </DialogTitle>

        <DialogContent sx={{ minWidth: 420 }}>
          {(phase === "waiting" || phase === "executing") && (
            <>
              <LinearProgress />
              <Typography sx={{ mt: 2 }}>
                {phase === "waiting"
                  ? `Starts in: ${formatSeconds(remaining)}`
                  : "Submitting to network..."}
              </Typography>
            </>
          )}

          {(phase === "done" || phase === "error") && (
            <Typography
              component="pre"
              sx={{
                mt: 1,
                mb: 0,
                whiteSpace: "pre-wrap",
                overflowWrap: "anywhere",
                fontFamily:
                  "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                fontSize: 13,
              }}
            >
              {resultText}
            </Typography>
          )}
        </DialogContent>

        <DialogActions>
          <Button variant="contained" onClick={() => setOpen(false)} disabled={!canClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
